import React, { useEffect, useState } from "react";
import { Card, IconButton, makeStyles, Theme } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { SignalRMessage, SignalRTypeEnum } from "./types";
import ExportSignalRMessage from "./ExportSignalRMessage";
import ImportSignalRMessage from "./ImportSignalRMessage";
import ReportSignalRMessage from "./ReportSignalRMessage";

type Props = {
  messages: SignalRMessage[];
  onClose: (id?: string) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  popupContainer: {
    position: "fixed",
    zIndex: 1400,
    bottom: theme.spacing(3),
    right: theme.spacing(2),
  },
  messageCard: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 360,
    minHeight: 150,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    transition: "all 200ms ease-in-out",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 6px -1px rgba(0, 0, 0, 0.6), 0 2px 4px -1px rgba(0, 0, 0, 0.4)"
        : "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)",
    backgroundColor: theme.palette.background.paper,
    opacity: 1,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    "&.MuiPaper-root": {
      backgroundColor: theme.palette.background.paper,
    },
  },
  messageCardClosing: {
    transform: "translateX(400px) scale(0.9)",
    visibility: "hidden" as const,
    opacity: 0,
  },
  messageCardVisible: {
    opacity: 1,
  },
  messageCardHidden: {
    transform: "scale(0.95)",
    visibility: "hidden" as const,
    opacity: 0,
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    padding: theme.spacing(0.5),
    color: "#666666",
    zIndex: 10,
  },
}));

const SignalRPopup = ({ messages, onClose }: Props) => {
  const classes = useStyles();
  const [isHovered, setIsHovered] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  const [visibleMessages, setVisibleMessages] = useState<Set<string>>(
    new Set()
  );
  const [closingMessages, setClosingMessages] = useState<Set<string>>(
    new Set()
  );

  const renderMessage = (message: SignalRMessage) => {
    const handleActionClick = () => {
      handleClose(message.id);
    };

    switch (message.type) {
      case SignalRTypeEnum.Report:
        return (
          <ReportSignalRMessage
            message={message}
            onActionClick={handleActionClick}
          />
        );
      case SignalRTypeEnum.Export:
        return (
          <ExportSignalRMessage
            message={message}
            onActionClick={handleActionClick}
          />
        );
      case SignalRTypeEnum.Import:
        return (
          <ImportSignalRMessage
            message={message}
            onActionClick={handleActionClick}
          />
        );
      default:
        return null;
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsHovered(false);
    }, 300);
    setHoverTimeout(timeout);
  };

  const handleClose = (messageId: string) => {
    setClosingMessages((prev) => new Set([...prev, messageId]));

    setTimeout(() => {
      onClose(messageId);
      setClosingMessages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }, 200);
  };

  useEffect(() => {
    messages.forEach((message) => {
      if (!visibleMessages.has(message.id)) {
        setTimeout(() => {
          setVisibleMessages((prev) => new Set([...prev, message.id]));
        }, 50);
      }
    });
  }, [messages, visibleMessages]);

  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  if (messages.length === 0) {
    return null;
  }

  const displayMessages = [...messages];
  const totalMessages = messages.length;

  return (
    <div
      className={classes.popupContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{ position: "relative" }}>
        {displayMessages.map((message, index) => {
          const reverseIndex = totalMessages - 1 - index;
          const isLatest = index === 0;
          const isVisible = visibleMessages.has(message.id);
          const isClosing = closingMessages.has(message.id);

          const stackVerticalOffset = reverseIndex * 5;
          const stackHorizontalOffset = reverseIndex * 5;
          const cardHeight = 150;
          const gap = 12;
          const hoverVerticalOffset = isHovered
            ? -reverseIndex * (cardHeight + gap)
            : 0;

          return (
            <Card
              key={message.id}
              className={`${classes.messageCard} ${
                isClosing
                  ? classes.messageCardClosing
                  : isVisible
                  ? classes.messageCardVisible
                  : classes.messageCardHidden
              }`}
              style={{
                transform: isClosing
                  ? "translateX(400px)"
                  : isHovered
                  ? `translateY(${hoverVerticalOffset}px)`
                  : `translate(${stackHorizontalOffset}px, -${stackVerticalOffset}px)`,
                zIndex: isHovered ? 100 + index : 100 - reverseIndex,
                pointerEvents: isClosing
                  ? "none"
                  : isHovered || isLatest
                  ? "auto"
                  : "none",
              }}
            >
              <IconButton
                size="small"
                className={classes.closeButton}
                onClick={() => handleClose(message.id)}
              >
                <Close fontSize="small" />
              </IconButton>
              {renderMessage(message)}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SignalRPopup;
